import { APP_CONFIG } from '@/constants/app';
import { ApiResponse } from '@/types/auth.types';
import {
  AuditLogListResponse,
  CreateInvitationDto,
  CreateVaultDto,
  GrantPermissionDto,
  InitializeVaultKeyDto,
  UnlockVaultDto,
  UpdateVaultDto,
  UploadDocumentDto,
  Vault,
  VaultAuditLogParams,
  VaultDocument,
  VaultEncryptionParamsResponse,
  VaultInvitation,
  VaultPermission,
  VaultResponse,
  VaultStatistics,
  MassCreateVaultDto,
} from '@/types/vault.types';
import { Employee } from '@/features/employees/types/employee.types';
import { tokenManager } from '@/utils/token-manager';
import axios from 'axios';

/**
 * Le résultat d'une activation en masse de coffres.
 *
 * `echecs` porte le détail : sans lui, l'écran ne pouvait afficher que
 * « 0 coffres-forts activés avec succès » — un message vert sur un échec
 * complet, qui a fait croire à une responsable paie que l'invitation était
 * partie (03/09/2026).
 */
export interface MassCreateVaultResult {
  created: number;
  errors: number;
  echecs?: { employeeId: string; nom: string | null; raison: string }[];
}

// Create axios instance for vault service
const vaultApi = axios.create({
  baseURL: `${APP_CONFIG.api.baseUrl}/vaults`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Setup automatic token refresh interceptors
tokenManager.setupInterceptors(vaultApi);

export const vaultService = {
  // ===== Vault Management =====

  /**
   * List all vaults for the current user
   */
  listVaults: async (): Promise<ApiResponse<Vault[]>> => {
    const response = await vaultApi.get<ApiResponse<Vault[]>>('');
    return response.data;
  },

  // ===== Contextual Vault Operations =====

  /**
   * Create a vault for a folder (company)
   */
  createFolderVault: async (
    folderId: string,
    data: CreateVaultDto
  ): Promise<ApiResponse<Vault>> => {
    const response = await vaultApi.post<ApiResponse<Vault>>(
      `/folder/${folderId}`,
      data
    );
    return response.data;
  },

  /**
   * Get the vault for a specific folder
   */
  getFolderVault: async (
    folderId: string
  ): Promise<ApiResponse<Vault | null>> => {
    try {
      const response = await vaultApi.get<ApiResponse<Vault>>(
        `/folder/${folderId}`
      );
      return response.data;
    } catch (cause) {
      const error = cause as HttpApiError;
      // Return null if vault doesn't exist (404)
      if (
        error.response?.status === 404 ||
        error.response?.data?.data === null
      ) {
        return {
          data: null,
          success: true,
          message: 'No vault found',
          meta: {
            timestamp: new Date().toISOString(),
            path: `/vaults/folder/${folderId}`,
            statusCode: 404,
          },
        };
      }
      throw error;
    }
  },

  /**
   * Create a vault for an employee
   */
  createEmployeeVault: async (
    employeeId: string,
    data: CreateVaultDto
  ): Promise<ApiResponse<Vault>> => {
    const response = await vaultApi.post<ApiResponse<Vault>>(
      `/employee/${employeeId}`,
      data
    );
    return response.data;
  },

  /**
   * Get the vault for a specific employee
   */
  getEmployeeVault: async (
    employeeId: string
  ): Promise<ApiResponse<Vault | null>> => {
    try {
      const response = await vaultApi.get<ApiResponse<Vault>>(
        `/employee/${employeeId}`
      );
      return response.data;
    } catch (cause) {
      const error = cause as HttpApiError;
      // Return null if vault doesn't exist (404)
      if (
        error.response?.status === 404 ||
        error.response?.data?.data === null
      ) {
        return {
          data: null,
          success: true,
          message: 'No vault found',
          meta: {
            timestamp: new Date().toISOString(),
            path: `/vaults/employee/${employeeId}`,
            statusCode: 404,
          },
        };
      }
      throw error;
    }
  },

  /**
   * Get employee vaults for a folder
   */
  getFolderEmployeeVaults: async (
    folderId: string
  ): Promise<
    ApiResponse<
      {
        vault: Vault;
        employeeName: string;
        employeeMatricule: string;
      }[]
    >
  > => {
    const response = await vaultApi.get<
      ApiResponse<
        {
          vault: Vault;
          employeeName: string;
          employeeMatricule: string;
        }[]
      >
    >(`/folder/${folderId}/employee-vaults`);
    return response.data;
  },

  /**
   * Get a specific vault by ID
   */
  getVault: async (vaultId: string): Promise<ApiResponse<Vault>> => {
    const response = await vaultApi.get<ApiResponse<Vault>>(`/${vaultId}`);
    return response.data;
  },

  /**
   * Create a new vault
   */
  createVault: async (data: CreateVaultDto): Promise<ApiResponse<Vault>> => {
    const response = await vaultApi.post<ApiResponse<Vault>>('', data);
    return response.data;
  },

  /**
   * Get employees without vault
   */
  getEmployeesWithoutVault: async (
    folderId: string
  ): Promise<ApiResponse<Employee[]>> => {
    const response = await vaultApi.get<ApiResponse<Employee[]>>(
      `/employees/no-vault`,
      {
        params: { folderId },
      }
    );
    return response.data;
  },

  /**
   * Mass create employee vaults
   */
  massCreateEmployeeVaults: async (
    data: MassCreateVaultDto & {
      employees: (MassCreateVaultDto['employees'][0] & {
        initializationData?: InitializeVaultKeyDto;
      })[];
    }
  ): Promise<ApiResponse<MassCreateVaultResult>> => {
    const response = await vaultApi.post<ApiResponse<MassCreateVaultResult>>(
      `/employees/mass-create`,
      data
    );
    return response.data;
  },

  /**
   * Update vault information
   */
  updateVault: async (
    vaultId: string,
    data: UpdateVaultDto
  ): Promise<ApiResponse<Vault>> => {
    const response = await vaultApi.put<ApiResponse<Vault>>(
      `/${vaultId}`,
      data
    );
    return response.data;
  },

  /**
   * Initialize vault encryption key (client-side encryption)
   * Sends pre-encrypted vault key to server (server never sees plaintext password or key)
   */
  initializeVaultKey: async (
    vaultId: string,
    data: InitializeVaultKeyDto
  ): Promise<ApiResponse<{ message: string }>> => {
    const response = await vaultApi.post<ApiResponse<{ message: string }>>(
      `/${vaultId}/initialize`,
      data
    );
    return response.data;
  },

  /**
   * Get vault encryption params (for client-side decryption)
   * Returns encrypted vault key, salt, and encryption params WITHOUT decrypting
   */
  getVaultEncryptionParams: async (
    vaultId: string
  ): Promise<ApiResponse<VaultEncryptionParamsResponse>> => {
    const response = await vaultApi.get<
      ApiResponse<VaultEncryptionParamsResponse>
    >(`/${vaultId}/encryption-params`);
    return response.data;
  },

  /**
   * Unlock vault with password (DEPRECATED - use getVaultEncryptionParams for client-side decryption)
   * This endpoint is kept for backward compatibility but should not be used in new code
   * @deprecated Use getVaultEncryptionParams instead
   */
  unlockVault: async (
    vaultId: string,
    data: UnlockVaultDto
  ): Promise<ApiResponse<VaultResponse>> => {
    const response = await vaultApi.post<ApiResponse<VaultResponse>>(
      `/${vaultId}/unlock`,
      data
    );
    return response.data;
  },

  /**
   * Suspend vault
   */
  suspendVault: async (
    vaultId: string
  ): Promise<ApiResponse<{ message: string }>> => {
    const response = await vaultApi.post<ApiResponse<{ message: string }>>(
      `/${vaultId}/suspend`
    );
    return response.data;
  },

  /**
   * Initiate vault closure (3-month notice)
   */
  initiateVaultClosure: async (
    vaultId: string
  ): Promise<ApiResponse<{ message: string; closureScheduledFor: string }>> => {
    const response = await vaultApi.post<
      ApiResponse<{ message: string; closureScheduledFor: string }>
    >(`/${vaultId}/closure/initiate`);
    return response.data;
  },

  /**
   * Finalize vault closure
   */
  finalizeVaultClosure: async (
    vaultId: string
  ): Promise<ApiResponse<{ message: string }>> => {
    const response = await vaultApi.post<ApiResponse<{ message: string }>>(
      `/${vaultId}/closure/finalize`
    );
    return response.data;
  },

  /**
   * Delete vault (soft delete)
   */
  deleteVault: async (
    vaultId: string
  ): Promise<ApiResponse<{ message: string }>> => {
    const response = await vaultApi.delete<ApiResponse<{ message: string }>>(
      `/${vaultId}`
    );
    return response.data;
  },

  /**
   * Get vault statistics
   */
  getVaultStatistics: async (
    vaultId: string
  ): Promise<ApiResponse<VaultStatistics>> => {
    const response = await vaultApi.get<ApiResponse<VaultStatistics>>(
      `/${vaultId}/statistics`
    );
    return response.data;
  },

  /**
   * Get vault activity (deposit history)
   * For CREATOR role - shows metadata without document content access
   */
  getVaultActivity: async (
    vaultId: string,
    params?: { page?: number; limit?: number }
  ): Promise<
    ApiResponse<{
      items: {
        uuid: string;
        type: string;
        originalFilename: string;
        originalSize: number;
        createdAt: string;
        period?: { year: number; month: number };
        employeeNumber?: string;
      }[];
      total: number;
      page: number;
      limit: number;
    }>
  > => {
    const response = await vaultApi.get(`/${vaultId}/activity`, { params });
    return response.data;
  },

  // ===== Document Management =====

  /**
   * List documents in a vault
   */
  listDocuments: async (
    vaultId: string,
    params?: {
      page?: number;
      limit?: number;
      type?: string;
      search?: string;
      year?: number;
      month?: number;
    }
  ): Promise<ApiResponse<VaultDocument[]>> => {
    const response = await vaultApi.get<ApiResponse<VaultDocument[]>>(
      `/${vaultId}/documents`,
      { params }
    );
    return response.data;
  },

  /**
   * Get available periods (year/month) for filtering documents
   */
  getDocumentPeriods: async (
    vaultId: string
  ): Promise<ApiResponse<{ year: number; month: number }[]>> => {
    const response = await vaultApi.get<
      ApiResponse<{ year: number; month: number }[]>
    >(`/${vaultId}/documents/periods`);
    return response.data;
  },

  /**
   * Get a specific document
   */
  getDocument: async (
    vaultId: string,
    documentId: string
  ): Promise<ApiResponse<VaultDocument>> => {
    const response = await vaultApi.get<ApiResponse<VaultDocument>>(
      `/${vaultId}/documents/${documentId}`
    );
    return response.data;
  },

  /**
   * Upload an encrypted document to vault
   * The file should already be encrypted client-side before calling this method
   */
  uploadDocument: async (
    vaultId: string,
    data: UploadDocumentDto
  ): Promise<ApiResponse<VaultDocument>> => {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('metadata', JSON.stringify(data.metadata));
    formData.append('type', data.type);

    if (data.period) {
      formData.append('period', JSON.stringify(data.period));
    }
    if (data.employeeNumber) {
      formData.append('employeeNumber', data.employeeNumber);
    }
    if (data.employeeDateOfBirth) {
      formData.append('employeeDateOfBirth', data.employeeDateOfBirth);
    }
    if (data.folderId) {
      formData.append('folderId', data.folderId);
    }

    const response = await vaultApi.post<ApiResponse<VaultDocument>>(
      `/${vaultId}/documents`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000, // 2 minutes timeout for large file uploads
      }
    );
    return response.data;
  },

  /**
   * Download a document from vault
   */
  downloadDocument: async (
    vaultId: string,
    documentId: string
  ): Promise<Blob> => {
    const response = await vaultApi.get(
      `/${vaultId}/documents/${documentId}/download`,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  },

  /**
   * Delete a document from vault
   */
  deleteDocument: async (
    vaultId: string,
    documentId: string
  ): Promise<ApiResponse<{ message: string }>> => {
    const response = await vaultApi.delete<ApiResponse<{ message: string }>>(
      `/${vaultId}/documents/${documentId}`
    );
    return response.data;
  },

  /**
   * Server-side upload: sends file in cleartext, server encrypts with vault public key
   */
  serverUploadDocument: async (
    vaultId: string,
    data: { file: File; documentType: string; year: number; month: number }
  ): Promise<ApiResponse<VaultDocument>> => {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('documentType', data.documentType);
    formData.append('year', data.year.toString());
    formData.append('month', data.month.toString());

    const response = await vaultApi.post<ApiResponse<VaultDocument>>(
      `/${vaultId}/documents/server-upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000,
      }
    );
    return response.data;
  },

  /**
   * Self-upload: l'employé dépose un document dans son propre vault
   * Le fichier est envoyé en clair et chiffré côté serveur
   */
  selfUploadDocument: async (
    vaultId: string,
    data: { file: File; documentType: string; year?: number; month?: number }
  ): Promise<ApiResponse<VaultDocument>> => {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('documentType', data.documentType);
    if (data.year !== undefined) {
      formData.append('year', data.year.toString());
    }
    if (data.month !== undefined) {
      formData.append('month', data.month.toString());
    }

    const response = await vaultApi.post<ApiResponse<VaultDocument>>(
      `/${vaultId}/documents/self-upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000,
      }
    );
    return response.data;
  },

  /**
   * Récupère le quota de stockage d'un vault
   */
  getStorageQuota: async (
    vaultId: string
  ): Promise<
    ApiResponse<{
      used: number;
      limit: number;
      remaining: number;
      percentage: number;
    }>
  > => {
    const response = await vaultApi.get<
      ApiResponse<{
        used: number;
        limit: number;
        remaining: number;
        percentage: number;
      }>
    >(`/${vaultId}/documents/storage-quota`);
    return response.data;
  },

  // ===== Permissions Management =====

  /**
   * List permissions for a vault
   */
  listPermissions: async (
    vaultId: string
  ): Promise<ApiResponse<VaultPermission[]>> => {
    const response = await vaultApi.get<ApiResponse<VaultPermission[]>>(
      `/${vaultId}/permissions`
    );
    return response.data;
  },

  /**
   * Grant permission to a user
   */
  grantPermission: async (
    vaultId: string,
    data: GrantPermissionDto
  ): Promise<ApiResponse<VaultPermission>> => {
    const response = await vaultApi.post<ApiResponse<VaultPermission>>(
      `/${vaultId}/permissions`,
      data
    );
    return response.data;
  },

  /**
   * Update permission
   */
  updatePermission: async (
    vaultId: string,
    permissionId: string,
    data: Partial<GrantPermissionDto>
  ): Promise<ApiResponse<VaultPermission>> => {
    const response = await vaultApi.put<ApiResponse<VaultPermission>>(
      `/${vaultId}/permissions/${permissionId}`,
      data
    );
    return response.data;
  },

  /**
   * Revoke permission
   */
  revokePermission: async (
    vaultId: string,
    permissionId: string
  ): Promise<ApiResponse<{ message: string }>> => {
    const response = await vaultApi.delete<ApiResponse<{ message: string }>>(
      `/${vaultId}/permissions/${permissionId}`
    );
    return response.data;
  },

  // ===== Invitations Management =====

  /**
   * List invitations for a vault
   */
  listInvitations: async (
    vaultId: string
  ): Promise<ApiResponse<VaultInvitation[]>> => {
    const response = await vaultApi.get<ApiResponse<VaultInvitation[]>>(
      `/${vaultId}/invitations`
    );
    return response.data;
  },

  /**
   * Create invitation
   */
  createInvitation: async (
    vaultId: string,
    data: CreateInvitationDto
  ): Promise<ApiResponse<VaultInvitation>> => {
    const response = await vaultApi.post<ApiResponse<VaultInvitation>>(
      `/${vaultId}/invitations`,
      data
    );
    return response.data;
  },

  /**
   * Get invitation by token (public endpoint - no auth required)
   */
  getInvitationByToken: async (token: string): Promise<VaultInvitation> => {
    // Public endpoint - no auth needed
    const response = await axios.get<{ data: VaultInvitation }>(
      `${APP_CONFIG.api.baseUrl}/invitations/${token}`
    );
    return response.data.data;
  },

  /**
   * Accept invitation (requires auth - uses vaultApi with token)
   */
  acceptInvitation: async (
    token: string
  ): Promise<{ message: string; vaultId: string }> => {
    // This endpoint requires auth, use vaultApi which has token interceptors
    const response = await vaultApi.post<{
      data: { message: string; vaultId: string };
    }>(`${APP_CONFIG.api.baseUrl}/invitations/accept`, { token });
    return response.data.data;
  },

  /**
   * Decline invitation (public endpoint - no auth required)
   */
  declineInvitation: async (token: string): Promise<VaultInvitation> => {
    const response = await axios.post<{ data: VaultInvitation }>(
      `${APP_CONFIG.api.baseUrl}/invitations/decline`,
      { token }
    );
    return response.data.data;
  },

  /**
   * Revoke invitation
   */
  revokeInvitation: async (
    vaultId: string,
    invitationId: string
  ): Promise<ApiResponse<{ message: string }>> => {
    const response = await vaultApi.delete<ApiResponse<{ message: string }>>(
      `/${vaultId}/invitations/${invitationId}`
    );
    return response.data;
  },

  /**
   * Resend invitation
   */
  resendInvitation: async (
    vaultId: string,
    invitationId: string,
    newEmail?: string
  ): Promise<ApiResponse<VaultInvitation>> => {
    const response = await vaultApi.post<ApiResponse<VaultInvitation>>(
      `/${vaultId}/invitations/${invitationId}/resend`,
      newEmail ? { email: newEmail } : {}
    );
    return response.data;
  },

  /**
   * Update folder contact email and resend invitation
   */
  updateFolderContact: async (
    vaultId: string,
    contactEmail: string
  ): Promise<ApiResponse<{ success: boolean; message: string }>> => {
    const response = await vaultApi.patch<
      ApiResponse<{ success: boolean; message: string }>
    >(`/${vaultId}/folder-contact`, { contactEmail });
    return response.data;
  },

  /**
   * Resend folder contact invitation
   */
  resendFolderInvitation: async (
    vaultId: string
  ): Promise<ApiResponse<{ success: boolean; message: string }>> => {
    const response = await vaultApi.post<
      ApiResponse<{ success: boolean; message: string }>
    >(`/${vaultId}/resend-folder-invitation`);
    return response.data;
  },

  // ===== Audit Logs =====

  /**
   * List audit logs for a vault
   */
  listAuditLogs: async (
    vaultId: string,
    params?: VaultAuditLogParams
  ): Promise<ApiResponse<AuditLogListResponse>> => {
    const response = await vaultApi.get<ApiResponse<AuditLogListResponse>>(
      `/${vaultId}/audit-logs`,
      { params }
    );
    return response.data;
  },

  /**
   * Verify audit log chain integrity
   */
  verifyAuditChain: async (
    vaultId: string
  ): Promise<ApiResponse<{ valid: boolean; message: string }>> => {
    const response = await vaultApi.get<
      ApiResponse<{ valid: boolean; message: string }>
    >(`/${vaultId}/audit-logs/verify`);
    return response.data;
  },

  /**
   * Export audit logs
   */
  exportAuditLogs: async (
    vaultId: string,
    format: 'json' | 'csv' = 'json'
  ): Promise<Blob> => {
    const response = await vaultApi.get(
      `/${vaultId}/audit-logs/export?format=${format}`,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  },

  // ===== Vault User Operations =====

  /**
   * Get vaults for vault_user (my-vaults endpoint)
   * Returns vaults where the user has access
   */
  getMyVaults: async (): Promise<ApiResponse<Vault[]>> => {
    const response = await vaultApi.get<ApiResponse<Vault[]>>('/my-vaults');
    return response.data;
  },

  /**
   * User-initialize a vault (for vault users who need to set up their vault)
   * This is the zero-knowledge initialization where user derives their own key
   */
  userInitializeVault: async (
    vaultId: string,
    data: InitializeVaultKeyDto
  ): Promise<ApiResponse<Vault>> => {
    const response = await vaultApi.post<ApiResponse<Vault>>(
      `/${vaultId}/user-initialize`,
      data
    );
    return response.data;
  },

  /**
   * Grant folder-specific granular permission
   */
  grantFolderPermission: async (
    vaultId: string,
    data: {
      userId: string;
      canViewDocuments: boolean;
      canReadContent: boolean;
      canUploadDocument: boolean;
      canSendToEmployeeVault: boolean;
      canViewEmployeeVaults: boolean;
      canDepositToEmployeeVault: boolean;
    }
  ): Promise<ApiResponse<VaultPermission>> => {
    const response = await vaultApi.post<ApiResponse<VaultPermission>>(
      `/${vaultId}/permissions/folder`,
      data
    );
    return response.data;
  },

  /**
   * Self-add: gestionnaire s'ajoute au vault avec toutes les permissions
   */
  selfAddToVault: async (
    vaultId: string
  ): Promise<ApiResponse<VaultPermission>> => {
    const response = await vaultApi.post<ApiResponse<VaultPermission>>(
      `/${vaultId}/permissions/self-add`
    );
    return response.data;
  },

  /**
   * Update folder-specific granular permission
   */
  updateFolderPermission: async (
    vaultId: string,
    permissionId: string,
    data: {
      canViewDocuments?: boolean;
      canReadContent?: boolean;
      canUploadDocument?: boolean;
      canSendToEmployeeVault?: boolean;
      canViewEmployeeVaults?: boolean;
      canDepositToEmployeeVault?: boolean;
    }
  ): Promise<ApiResponse<VaultPermission>> => {
    const response = await vaultApi.put<ApiResponse<VaultPermission>>(
      `/${vaultId}/permissions/${permissionId}/folder`,
      data
    );
    return response.data;
  },

  /**
   * Migrate a FOLDER vault from ZERO_KNOWLEDGE to SERVER_SIDE encryption
   */
  migrateToServerEncryption: async (
    vaultId: string,
    password: string
  ): Promise<
    ApiResponse<{
      success: boolean;
      documentsProcessed: number;
      errors: number;
    }>
  > => {
    const response = await vaultApi.post<
      ApiResponse<{
        success: boolean;
        documentsProcessed: number;
        errors: number;
      }>
    >(`/${vaultId}/migrate-to-server-encryption`, { password });
    return response.data;
  },

  /**
   * Upload a document to a SERVER_SIDE vault (plain file, encrypted server-side)
   */
  uploadServerSideDocument: async (
    vaultId: string,
    file: File,
    type: string,
    metadata?: {
      period?: { year: number; month: number };
      employeeNumber?: string;
    }
  ): Promise<ApiResponse<VaultDocument>> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    if (metadata?.period) {
      formData.append('year', String(metadata.period.year));
      formData.append('month', String(metadata.period.month));
    }
    if (metadata?.employeeNumber) {
      formData.append('employeeNumber', metadata.employeeNumber);
    }
    const response = await vaultApi.post<ApiResponse<VaultDocument>>(
      `/${vaultId}/documents/server-upload`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  /**
   * Migrate a legacy document from ZERO_KNOWLEDGE to SERVER_SIDE encryption
   * Sends decrypted content to server for re-encryption
   */
  migrateDocumentToServer: async (
    vaultId: string,
    documentId: string,
    file: Blob
  ): Promise<ApiResponse<VaultDocument>> => {
    const formData = new FormData();
    formData.append('file', file, 'migrated-document');

    const response = await vaultApi.post<ApiResponse<VaultDocument>>(
      `/${vaultId}/documents/${documentId}/migrate-to-server`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000,
      }
    );
    return response.data;
  },

  /**
   * Get pending initialization status for a vault
   */
  getPendingInitialization: async (
    vaultId: string
  ): Promise<{
    data: {
      isPending: boolean;
      vault: { uuid: string; name: string; status: string } | null;
    };
  }> => {
    type PendingInitialization = {
      vaultId: string;
      name: string;
      status: string;
      needsInitialization: boolean;
    };
    const response = await vaultApi.get<
      { data: PendingInitialization } | PendingInitialization
    >(`/${vaultId}/pending-initialization`);
    // Map backend response to frontend expected format
    // Backend wraps in { data: { ... } }
    const backendData =
      'data' in response.data ? response.data.data : response.data;
    return {
      data: {
        isPending: backendData.needsInitialization,
        vault: {
          uuid: backendData.vaultId,
          name: backendData.name,
          status: backendData.status,
        },
      },
    };
  },
};
